/**
 * 奖池由什么组成。
 *
 * <p>需求：让用户**清晰快速**地看懂这笔钱怎么来的（2026-08-19 评审）。
 *
 * <p>构成只有两项，不多不少：
 * <ul>
 * <li><b>本轮起始</b>（`seed`）—— 上一次开奖后剩下的那笔，直接成为新一轮的种子；
 *     一次都没开过时，它是平台一次性的开办资金。
 * <li><b>本轮累积</b>（`pot`）—— 这一轮里玩家投进来的部分。
 * </ul>
 *
 * <p>`in / out / adjust` 是**对账**用的四则运算，不是构成 —— 把它们摆给玩家看，
 * 他要先做一遍减法才知道池子里有什么。恒等式那一行留给较真的人，构成给所有人。
 */

export type LedgerTotals = {
  in: string;
  out: string;
  seed: string;
  adjust: string;
  pot: string;
  balanced: boolean;
  /** 种子来自哪一期开奖；平台开办资金时为 null。 */
  seedFromRound?: number | null;
};

export type PoolPart = {
  key: 'seed' | 'pot';
  cents: number;
  /** 占当前奖池的比例，0–1。奖池为 0 时两项都是 0。 */
  share: number;
};

export type PoolComposition = {
  totalCents: number;
  parts: PoolPart[];
  /** 种子是继承来的还是平台开办的 —— 决定那一行怎么措辞 */
  seedOrigin: 'carried' | 'initial';
  seedFromRound: number | null;
};

function cents(value: string | number | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function poolComposition(totals: LedgerTotals | null | undefined): PoolComposition | null {
  if (!totals) return null;
  const seed = cents(totals.seed);
  const pot = cents(totals.pot);
  const total = seed + pot;
  // 负数或零：不画构成。宁可不显示，也不要画一根方向不明的条
  if (total <= 0 || seed < 0 || pot < 0) return null;
  const from = totals.seedFromRound;
  return {
    totalCents: total,
    parts: [
      { key: 'seed', cents: seed, share: seed / total },
      { key: 'pot', cents: pot, share: pot / total },
    ],
    // **只有真的开出过头奖才叫「继承」**。把开办资金说成上一轮留存是假话。
    seedOrigin: typeof from === 'number' && from > 0 ? 'carried' : 'initial',
    seedFromRound: typeof from === 'number' && from > 0 ? from : null,
  };
}
